import React, { useState, useEffect, useContext } from "react";
import { useForm, Controller } from "react-hook-form";
import {
  Modal,
  Row,
  Col,
  Card,
  Form,
  Button,
  Alert,
  Spinner,
  ProgressBar,
  Badge,
  InputGroup,
} from "react-bootstrap";
import {
  IconBuilding,
  IconUsers,
  IconSettings,
  IconCheck,
  IconX,
  IconArrowRight,
  IconArrowLeft,
  IconMail,
  IconPhone,
  IconMapPin,
  IconId,
  IconSparkles,
  IconAlertCircle,
  IconUserPlus,
  IconCrown,
  IconToggleLeft,
  IconToggleRight,
  IconEye,
  IconEyeOff,
  IconRefresh,
  IconCopy,
  IconUser,
} from "@tabler/icons-react";
import NumberFormat from "react-number-format";
import Swal from "sweetalert2";
import SubaccountService from "../services/OrganizationSubAccountServices";
import UserContext from "../../../contexts/UserContext";
import "../css/createSubAccountModal.css";

const CreateSubaccountModal = ({ show, onHide, organization, onSuccess }) => {
  const { getUserPlan } = useContext(UserContext);
  
  // Função para obter limite de usuários baseado no plano
  const getUserLimitByPlan = () => {
    const plan = getUserPlan();
    switch (plan) {
      case 3: // Starter
        return 1;
      case 4: // PRO
        return 3;
      case 2: // Business/Founder
      case 5: // Scale
        return 5;
      default:
        return 1;
    }
  };

  // Configuração do react-hook-form
  const {
    control,
    handleSubmit,
    setValue,
    watch,
    reset,
    trigger,
    getValues,
    clearErrors,
    setError,
    formState: { errors: formErrors, isValid },
  } = useForm({
    mode: "onChange",
    defaultValues: {
      // Tipo de cliente
      client_type: "juridical", // "physical" ou "juridical"

      // Dados da subconta
      company_name: "",
      email: "",
      cpf_cnpj: "",
      business_segment: "",
      name: "",
      description: "",
      phone: "",
      company_type: "LIMITED",
      person_type: 1,
      title: "",
      max_users_allowed: 1, // Será atualizado baseado no plano
      business_categories: [],

      // Dados específicos para pessoa física
      physical_name: "",
      physical_cpf: "",
      physical_email: "",

      // Dados do admin
      admin_first_name: "",
      admin_last_name: "",
      admin_email: "",
      admin_phone: "",
      admin_cpf: "",
      admin_birthday: "",

      // Configurações
      send_invitation: true,
      features: [],
      feature_limits: {},
      feature_notes: {},
    },
  });

  // Estados de controle
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [availableFeatures, setAvailableFeatures] = useState([]);
  const [featuresGrouped, setFeaturesGrouped] = useState({});
  const [selectedFeatures, setSelectedFeatures] = useState(new Set());
  const [featureLimits, setFeatureLimits] = useState({});
  const [featureNotes, setFeatureNotes] = useState({});
  const [validatingCPF, setValidatingCPF] = useState(false);
  const [validatingEmail, setValidatingEmail] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [validatingSubaccountData, setValidatingSubaccountData] = useState(false);

  const watchedValues = watch();
  const totalSteps = 4;
  const clientType = watch("client_type");

  const stepTitles = {
    1: clientType === "physical" ? "Dados Pessoais" : "Dados da Empresa",
    2: "Funcionalidades",
    3: "Usuário Admin",
    4: "Revisão Final",
  };

  const businessSegments = [
    { value: "1", label: "Imobiliário" },
    { value: "2", label: "Saúde e Bem estar" },
    { value: "3", label: "Varejo" },
    { value: "4", label: "Gastronomia" },
    { value: "5", label: "TI e Telecomunicações" },
    { value: "6", label: "Educação" },
    { value: "7", label: "Serviços Financeiros" },
    { value: "8", label: "Logística e Transporte" },
    { value: "9", label: "Construção e Engenharia" },
    { value: "10", label: "Agronegócio" },
    { value: "11", label: "Turismo e Hospitalidade" },
    { value: "12", label: "Indústria e Manufatura" },
    { value: "13", label: "Entretenimento e Mídia" },
    { value: "14", label: "Outros" },
  ];

  const companyTypes = [
    { value: "MEI", label: "Micro Empreendedor Individual" },
    { value: "LIMITED", label: "Empresa Limitada" },
    { value: "INDIVIDUAL", label: "Empresa Individual" },
    { value: "ASSOCIATION", label: "Associação" },
  ];

  // Atualizar person_type baseado no tipo de cliente
  useEffect(() => {
    if (clientType === "physical") {
      setValue("person_type", 2);
      setValue("company_type", "PF");
      // Limpar erros de campos de pessoa jurídica
      clearErrors(["company_name", "email", "cpf_cnpj"]);
    } else {
      setValue("person_type", 1);
      if (watchedValues.company_type === "PF") {
        setValue("company_type", "LIMITED");
      }
      // Limpar erros de campos de pessoa física
      clearErrors(["physical_name", "physical_email", "physical_cpf"]);
    }
  }, [clientType, setValue, watchedValues.company_type, clearErrors]);

  // Carregar features disponíveis ao abrir modal
  useEffect(() => {
    if (show) {
      loadAvailableFeatures();
      resetForm();
      // Definir limite de usuários baseado no plano
      setValue("max_users_allowed", getUserLimitByPlan());
    }
  }, [show, setValue]);

  // Debounce para validações de CPF do admin
  useEffect(() => {
    const timer = setTimeout(() => {
      if (
        watchedValues.admin_cpf &&
        watchedValues.admin_cpf.replace(/\D/g, "").length === 11
      ) {
        validateCPF(watchedValues.admin_cpf);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [watchedValues.admin_cpf]);

  // Debounce para validações de email do admin
  useEffect(() => {
    const timer = setTimeout(() => {
      if (
        watchedValues.admin_email &&
        watchedValues.admin_email.includes("@")
      ) {
        validateEmail(watchedValues.admin_email);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [watchedValues.admin_email]);

  const loadAvailableFeatures = async () => {
    try {
      setLoading(true);
      const features = await SubaccountService.getAvailableFeatures();
      setAvailableFeatures(features);

      const grouped = SubaccountService.groupFeaturesByCategory(features);
      setFeaturesGrouped(grouped);

      // Pre-selecionar features padrão
      const defaultFeatures = features.filter((f) =>
        [
          "Chat",
          "Meus Agentes",
          "Base de Clientes",
          "Pipeline CRM",
          "Agenda",
          "Templates",
          "Fluxos Ativos",
          "WhatsApp",
          "Minha Empresa",
          "Tags & Categorias",
        ].includes(f.name)
      );

      const defaultFeatureIds = new Set(defaultFeatures.map((f) => f.id));
      setSelectedFeatures(defaultFeatureIds);

      // Definir limites padrão
      const defaultLimits = {};
      defaultFeatures.forEach((feature) => {
        if (
          feature.name === "Meus Agentes" ||
          feature.name === "Criar Agente"
        ) {
          defaultLimits[feature.id] = 1; // 1 agente para empresa
        }
      });
      setFeatureLimits(defaultLimits);
    } catch (error) {
      console.error("Erro ao carregar features:", error);
      Swal.fire({
        icon: "error",
        title: "Erro",
        text: "Erro ao carregar funcionalidades disponíveis",
        confirmButtonColor: "#dc3545",
      });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setCurrentStep(1);
    setSelectedFeatures(new Set());
    setFeatureLimits({});
    setFeatureNotes({});
    setPreviewMode(false);
    reset();
  };

  // Validar CPF com debounce
  const validateCPF = async (cpf) => {
    if (cpf && cpf.replace(/\D/g, "").length === 11) {
      setValidatingCPF(true);
      try {
        await SubaccountService.validateCPF(cpf, getValues("admin_email"));
        clearErrors("admin_cpf");
        return true;
      } catch (error) {
        setError("admin_cpf", {
          type: "manual",
          message: "CPF já cadastrado ou inválido",
        });
        return false;
      } finally {
        setValidatingCPF(false);
      }
    }
    return true;
  };

  // Validar email único
  const validateEmail = async (email) => {
    if (email && email.includes("@")) {
      setValidatingEmail(true);
      try {
        // Simular validação de email único
        const existingEmails = ["admin@teste.com", "contato@empresa.com"];
        if (existingEmails.includes(email.toLowerCase())) {
          setError("admin_email", {
            type: "manual",
            message: "Este email já está em uso",
          });
          return false;
        }
        clearErrors("admin_email");
        return true;
      } catch (error) {
        return false;
      } finally {
        setValidatingEmail(false);
      }
    }
    return true;
  };

  // Navegação entre steps
  const nextStep = async () => {
    const isStepValid = await validateCurrentStep();
    if (isStepValid) {
      setCurrentStep((prev) => Math.min(prev + 1, totalSteps));
    }
  };

  const prevStep = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const goToStep = async (step) => {
    if (step <= currentStep) {
      setCurrentStep(step);
    } else {
      const isValid = await validateStepsUpTo(step - 1);
      if (isValid) {
        setCurrentStep(step);
      }
    }
  };

  // Validações por step
  const validateCurrentStep = async () => {
    switch (currentStep) {
      case 1:
        // Primeiro validar os campos localmente
        let localValidation;
        if (clientType === "physical") {
          localValidation = await trigger([
            "physical_name",
            "physical_cpf",
            "physical_email",
            "business_segment",
          ]);
        } else {
          localValidation = await trigger([
            "company_name",
            "email",
            "cpf_cnpj",
            "business_segment",
          ]);
        }

        // Se validação local passou, fazer validação remota
        if (localValidation) {
          setValidatingSubaccountData(true);
          try {
            const validationData = {
              cpf_cnpj: clientType === "physical" ? watchedValues.physical_cpf : watchedValues.cpf_cnpj,
              email: clientType === "physical" ? watchedValues.physical_email : watchedValues.email,
              phone: watchedValues.phone,
              company_name: clientType === "physical" ? watchedValues.physical_name : watchedValues.company_name,
              physical_name: clientType === "physical" ? watchedValues.physical_name : null,
            };

            const result = await SubaccountService.validateSubaccountDataRemote(validationData);
            
            if (!result.success) {
              // Tratar diferentes tipos de erro
              if (result.statusCode === 400 && result.details) {
                // Erro de duplicata - mostrar detalhes específicos
                if (Array.isArray(result.details)) {
                  const errorMessages = result.details.map(detail => 
                    `• ${detail.message || detail.description}`
                  ).join('<br>');
                  
                  await Swal.fire({
                    icon: "error",
                    title: "Dados já existem no sistema",
                    html: `
                      <div style="text-align: left;">
                        <p><strong>Os seguintes dados já estão cadastrados:</strong></p>
                        <div style="margin: 15px 0; padding: 15px; background: #fff5f5; border-left: 4px solid #dc3545; border-radius: 4px;">
                          ${errorMessages}
                        </div>
                        <p style="color: #6c757d; font-size: 14px;">
                          Verifique os dados informados ou entre em contato com o suporte se acredita que isso é um erro.
                        </p>
                      </div>
                    `,
                    confirmButtonColor: "#dc3545",
                  });
                } else {
                  // Erro de limite ou outro erro estruturado
                  if (result.details?.requires_payment) {
                    // Erro de limite com opção de contratação
                    const contractResult = await Swal.fire({
                      icon: "info",
                      title: result.error,
                      html: `
                        <div style="text-align: left;">
                          <p>${result.details.reason || result.error}</p>
                          <p><strong>Valor adicional:</strong> R$ ${result.details.extra_value}</p>
                          <div style="background: #e3f2fd; border: 1px solid #2196f3; border-radius: 8px; padding: 16px; margin: 16px 0;">
                            <p style="color: #1976d2; font-size: 14px; margin: 0;">
                              <strong>💡 Solução:</strong> Expanda seu plano para criar mais subcontas.
                            </p>
                          </div>
                        </div>
                      `,
                      showCancelButton: true,
                      confirmButtonText: "🚀 Contratar Agora",
                      cancelButtonText: "Cancelar",
                      confirmButtonColor: "var(--primary)",
                      cancelButtonColor: "#6c757d",
                      customClass: {
                        confirmButton: "btn btn-primary",
                        cancelButton: "btn btn-secondary",
                      },
                      buttonsStyling: false,
                    });

                    if (contractResult.isConfirmed) {
                      // Redirecionar para /subscriptions
                      window.location.href = '/subscriptions';
                    }
                  } else {
                    // Outros erros estruturados sem pagamento
                    await Swal.fire({
                      icon: "error",
                      title: result.error,
                      html: `
                        <div style="text-align: left;">
                          <p>${result.details?.reason || result.error}</p>
                        </div>
                      `,
                      confirmButtonColor: "#dc3545",
                    });
                  }
                }
              } else {
                // Erro genérico
                await Swal.fire({
                  icon: "error",
                  title: "Erro na Validação",
                  text: result.error,
                  confirmButtonColor: "#dc3545",
                });
              }
              return false;
            }
            
            return true;
          } catch (error) {
            console.error("Erro na validação remota:", error);
            await Swal.fire({
              icon: "error",
              title: "Erro de Comunicação",
              text: "Não foi possível validar os dados. Tente novamente.",
              confirmButtonColor: "#dc3545",
            });
            return false;
          } finally {
            setValidatingSubaccountData(false);
          }
        }
        return false;
      case 2:
        if (selectedFeatures.size === 0) {
          Swal.fire({
            icon: "warning",
            title: "Funcionalidades necessárias",
            text: "Selecione pelo menos uma funcionalidade para continuar",
            confirmButtonColor: "#f59e0b",
          });
          return false;
        }
        return true;
      case 3:
        return await trigger([
          "admin_first_name",
          "admin_email",
          "admin_phone",
          "admin_cpf"
        ]);
      case 4:
        return isValid && selectedFeatures.size > 0;
      default:
        return true;
    }
  };

  const validateStepsUpTo = async (step) => {
    switch (step) {
      case 1:
        return await validateCurrentStep();
      case 2:
        let step1Valid;
        if (clientType === "physical") {
          step1Valid = await trigger([
            "physical_name",
            "physical_cpf",
            "physical_email",
            "business_segment",
          ]);
        } else {
          step1Valid = await trigger([
            "company_name",
            "email",
            "cpf_cnpj",
            "business_segment",
          ]);
        }
        return step1Valid && selectedFeatures.size > 0;
      case 3:
        const steps12Valid = await validateStepsUpTo(2);
        const step3Valid = await trigger([
          "admin_first_name",
          "admin_email",
          "admin_phone",
        ]);
        return steps12Valid && step3Valid;
      default:
        return true;
    }
  };

  // Manipular seleção de features
  const toggleFeature = (featureId) => {
    const newSelected = new Set(selectedFeatures);
    if (newSelected.has(featureId)) {
      newSelected.delete(featureId);
      // Remover limite se feature foi deselecionada
      const newLimits = { ...featureLimits };
      delete newLimits[featureId];
      setFeatureLimits(newLimits);

      const newNotes = { ...featureNotes };
      delete newNotes[featureId];
      setFeatureNotes(newNotes);
    } else {
      newSelected.add(featureId);
    }
    setSelectedFeatures(newSelected);
  };

  // Selecionar todas features de uma categoria
  const toggleAllFeaturesInCategory = (categoryFeatures) => {
    const categoryIds = categoryFeatures.map((f) => f.id);
    const allSelected = categoryIds.every((id) => selectedFeatures.has(id));

    const newSelected = new Set(selectedFeatures);
    if (allSelected) {
      // Desselecionar todos
      categoryIds.forEach((id) => newSelected.delete(id));
    } else {
      // Selecionar todos
      categoryIds.forEach((id) => newSelected.add(id));
    }
    setSelectedFeatures(newSelected);
  };

  // Submeter formulário
  const onSubmit = async (data) => {
    setLoading(true);
    try {
      let subaccountData;

      if (data.client_type === "physical") {
        subaccountData = {
          company_name: data.physical_name,
          email: data.physical_email,
          cpf_cnpj: data.physical_cpf,
          business_segment: data.business_segment,
          person_type: 2,
          organization_id: organization.id,
          name: data.physical_name,
          phone: data.phone || "",
          company_type: "PF",
          title: data.title || "",
          max_users_allowed: data.max_users_allowed || getUserLimitByPlan(),
          description: data.description || "",
          subaccount: 1
        };
      } else {
        subaccountData = {
          ...SubaccountService.formatSubaccountForSubmission(data),
          organization_id: organization.id,
          subaccount: 1
        };
      }

      const adminData = SubaccountService.formatAdminForSubmission({
        first_name: data.admin_first_name,
        last_name: data.admin_last_name,
        email: data.admin_email,
        phone: data.admin_phone,
        cpf: data.admin_cpf,
        birthday: data.admin_birthday,
      });

      const featuresData = {
        feature_ids: Array.from(selectedFeatures),
        limits: featureLimits,
        notes: featureNotes,
      };

      const result = await SubaccountService.createCompleteSubaccount(
        subaccountData,
        adminData,
        featuresData,
        data.send_invitation
      );

      // Sucesso
      await Swal.fire({
        icon: "success",
        title: "🎉 Subconta Criada!",
        html: `
          <div style="text-align: left; padding: 20px;">
            <p><strong>${
              data.client_type === "physical" ? "Subconta" : "Empresa"
            }:</strong> ${
          data.client_type === "physical"
            ? data.physical_name
            : data.company_name
        }</p>
            <p><strong>Admin:</strong> ${data.admin_first_name} ${
          data.admin_last_name || ""
        }</p>
            <p><strong>Email:</strong> ${data.admin_email}</p>
            <p><strong>Funcionalidades:</strong> ${selectedFeatures.size}</p>
            ${
              data.send_invitation
                ? "<p><strong>✉️ Convite enviado por email</strong></p>"
                : ""
            }
            <br/>
            <p style="color: #6c757d; font-size: 14px;">
              A subconta foi criada com sucesso e já está ativa!
            </p>
          </div>
        `,
        confirmButtonText: "Continuar",
        confirmButtonColor: "#28a745",
      });

      if (onSuccess) {
        onSuccess(result);
      }
      handleClose();
      setTimeout(() => {
        window.location.reload();
      }, 3000);
    } catch (error) {
      console.error("Erro ao criar subconta:", error);

      // Tratamento específico baseado na resposta estruturada da API
      if (error.response?.data) {
        const responseData = error.response.data;
        
        // Se a resposta tem success: false, é uma resposta estruturada
        if (responseData.success === false) {
          const { error: errorMsg, details, field } = responseData;
          
          // Tratamento para limite de subcontas excedido
          if (errorMsg === "Limite de subcontas excedido" && details?.requires_payment) {
            const contractResult = await Swal.fire({
              icon: "info",
              title: "Limite de Subcontas Excedido",
              html: `
                <div style="text-align: left;">
                  <p>${details.reason}</p>
                  <p><strong>Valor adicional:</strong> R$ ${details.extra_value}</p>
                  <p><strong>Uso atual:</strong> ${details.current_usage}/${details.total_limit} subcontas</p>
                  <div style="background: #e3f2fd; border: 1px solid #2196f3; border-radius: 8px; padding: 16px; margin: 16px 0;">
                    <p style="color: #1976d2; font-size: 14px; margin: 0;">
                      <strong>💡 Solução:</strong> Expanda seu plano para criar mais subcontas.
                    </p>
                  </div>
                </div>
              `,
              showCancelButton: true,
              confirmButtonText: "🚀 Contratar Agora",
              cancelButtonText: "Cancelar",
              confirmButtonColor: "var(--primary)",
              cancelButtonColor: "#6c757d",
            });

            if (contractResult.isConfirmed) {
              window.location.href = '/subscriptions';
            }
            return;
          }
          
          // Tratamento para dados duplicados/já existentes
          if (errorMsg === "Dados já existem no sistema" && Array.isArray(details)) {
            const errorMessages = details.map(detail => 
              `• ${detail.message || detail.description || detail}`
            ).join('<br>');
            
            await Swal.fire({
              icon: "error",
              title: "Dados já cadastrados",
              html: `
                <div style="text-align: left;">
                  <p><strong>Os seguintes dados já estão em uso:</strong></p>
                  <div style="margin: 15px 0; padding: 15px; background: #fff5f5; border-left: 4px solid #dc3545; border-radius: 4px;">
                    ${errorMessages}
                  </div>
                  <p style="color: #6c757d; font-size: 14px;">
                    Verifique os dados informados ou entre em contato com o suporte se acredita que isso é um erro.
                  </p>
                </div>
              `,
              confirmButtonColor: "#dc3545",
            });
            return;
          }
          
          // Tratamento para erros de validação de dados
          if (errorMsg.includes("inválidos") && details) {
            const validationErrors = [];
            
            // Processar erros de validação do serializer
            if (typeof details === "object") {
              Object.entries(details).forEach(([field, errors]) => {
                const fieldErrors = Array.isArray(errors) ? errors : [errors];
                fieldErrors.forEach(err => {
                  validationErrors.push(`• ${field}: ${err}`);
                });
              });
            }
            
            await Swal.fire({
              icon: "error",
              title: "Dados Inválidos",
              html: `
                <div style="text-align: left;">
                  <p><strong>Corrija os seguintes campos:</strong></p>
                  <div style="margin: 15px 0; padding: 15px; background: #fff5f5; border-left: 4px solid #dc3545; border-radius: 4px;">
                    ${validationErrors.join('<br>')}
                  </div>
                </div>
              `,
              confirmButtonColor: "#dc3545",
            });
            return;
          }
          
          // Tratamento para erro de organização não encontrada
          if (errorMsg.includes("organização")) {
            await Swal.fire({
              icon: "warning",
              title: "Organização Necessária",
              text: errorMsg,
              confirmButtonText: "Entendi",
              confirmButtonColor: "#f59e0b",
            });
            return;
          }
          
          // Erro estruturado genérico
          await Swal.fire({
            icon: "error",
            title: "Erro na Criação",
            text: errorMsg,
            confirmButtonColor: "#dc3545",
          });
          return;
        }
        
        // Fallback para respostas não estruturadas
        let errorMessage = "Erro ao criar subconta";
        if (typeof responseData === "string") {
          errorMessage = responseData;
        } else if (typeof responseData === "object") {
          errorMessage = responseData.message || responseData.error || Object.values(responseData)[0] || errorMessage;
        }
        
        // Tratamento para status 401 (não autorizado)
        if (error.response?.status === 401) {
          Swal.fire({
            icon: "warning",
            title: "Acesso Negado",
            text: errorMessage,
            confirmButtonColor: "#f59e0b",
          });
          return;
        }
        
        // Erro genérico com mensagem do servidor
        Swal.fire({
          icon: "error",
          title: "Erro ao criar subconta",
          text: errorMessage,
          confirmButtonColor: "#dc3545",
        });
        
      } else {
        // Erro de rede ou sem resposta do servidor
        Swal.fire({
          icon: "error",
          title: "Erro de Comunicação",
          text: "Não foi possível conectar ao servidor. Verifique sua conexão e tente novamente.",
          confirmButtonColor: "#dc3545",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // Reset modal ao fechar
  const handleClose = () => {
    resetForm();
    onHide();
  };

  // Toggle preview mode
  const togglePreview = () => {
    setPreviewMode(!previewMode);
  };

  // Renderizar step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return renderCompanyStep();
      case 2:
        return renderFeaturesStep();
      case 3:
        return renderAdminStep();
      case 4:
        return renderReviewStep();
      default:
        return null;
    }
  };

  // Componente separado para seletor de tipo de cliente
  const renderClientTypeSelector = () => (
    <Row className="mb-4">
      <Col xs={12}>
        <Form.Label className="subaccount-modal__label mb-3">
          <IconUser size={16} />
          Tipo de Subconta *
        </Form.Label>
        <Controller
          name="client_type"
          control={control}
          render={({ field }) => (
            <div className="d-flex gap-4">
              <Form.Check
                type="radio"
                id="client_type_juridical"
                label="Pessoa Jurídica"
                value="juridical"
                checked={field.value === "juridical"}
                onChange={() => field.onChange("juridical")}
                className="subaccount-modal__radio"
              />
              <Form.Check
                type="radio"
                id="client_type_physical"
                label="Pessoa Física"
                value="physical"
                checked={field.value === "physical"}
                onChange={() => field.onChange("physical")}
                className="subaccount-modal__radio ml-2"
              />
            </div>
          )}
        />
      </Col>
    </Row>
  );

  // Formulário exclusivo para Pessoa Física
  const renderPhysicalPersonForm = () => (
    <div className="subaccount-modal__step-content">
      <div className="subaccount-modal__step-header">
        <IconUser size={32} className="subaccount-modal__step-icon" />
        <h3>Dados Pessoais</h3>
        <p>Informações pessoais do cliente</p>
      </div>

      {renderClientTypeSelector()}

      {/* Campos exclusivos para Pessoa Física */}
      <Row>
          <Col xs={12} md={6}>
            <Form.Group className="mb-3">
              <Form.Label className="subaccount-modal__label">
                <IconUser size={16} />
                Nome Completo *
              </Form.Label>
              <Controller
                name="physical_name"
                control={control}
                rules={{
                  required: "Nome é obrigatório",
                  minLength: {
                    value: 3,
                    message: "Nome deve ter pelo menos 3 caracteres",
                  },
                  maxLength: {
                    value: 128,
                    message: "Nome deve ter no máximo 128 caracteres",
                  },
                }}
                render={({ field }) => (
                  <Form.Control
                    {...field}
                    type="text"
                    placeholder="Ex: João da Silva Santos"
                    className={`subaccount-modal__input ${
                      formErrors.physical_name ? "is-invalid" : ""
                    }`}
                    maxLength={128}
                  />
                )}
              />
              {formErrors.physical_name && (
                <div className="invalid-feedback">
                  {formErrors.physical_name.message}
                </div>
              )}
            </Form.Group>
          </Col>

          <Col xs={12} md={6}>
            <Form.Group className="mb-3">
              <Form.Label className="subaccount-modal__label">
                <IconId size={16} />
                CPF *
              </Form.Label>
              <Controller
                name="physical_cpf"
                control={control}
                rules={{
                  required: "CPF é obrigatório",
                  validate: (value) => {
                    const clean = value?.replace(/\D/g, "") || "";
                    console.log(clean);
                    if (clean.length !== 11) {
                      return "CPF deve ter 11 dígitos";
                    }
                    return true;
                  },
                }}
                render={({ field }) => (
                  <NumberFormat
                    {...field}
                    format="###.###.###-##"
                    mask="_"
                    customInput={Form.Control}
                    placeholder="000.000.000-00"
                    className={`subaccount-modal__input ${
                      formErrors.physical_cpf ? "is-invalid" : ""
                    }`}
                  />
                )}
              />
              {console.log(formErrors.physical_cpf)}
              {formErrors.physical_cpf && (
                <div className="invalid-feedback">CPF Inválido!</div>
              )}
            </Form.Group>
          </Col>

          <Col xs={12} md={6}>
            <Form.Group className="mb-3">
              <Form.Label className="subaccount-modal__label">
                <IconMail size={16} />
                Email para Contato *
              </Form.Label>
              <Controller
                name="physical_email"
                control={control}
                rules={{
                  required: clientType === "physical" ? "Email é obrigatório" : false,
                  validate: (value) => {
                    // Só validar quando for pessoa física
                    if (clientType === "physical" && value) {
                      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                      if (!emailRegex.test(value)) {
                        return "Email inválido para pessoa física";
                      }
                    }
                    return true;
                  },
                }}
                render={({ field }) => (
                  <Form.Control
                    {...field}
                    type="email"
                    placeholder="joao@email.com"
                    className={`subaccount-modal__input ${
                      formErrors.physical_email ? "is-invalid" : ""
                    }`}
                  />
                )}
              />
              {formErrors.physical_email && (
                <div className="invalid-feedback">
                  {formErrors.physical_email.message}
                </div>
              )}
            </Form.Group>
          </Col>

          <Col xs={12} md={6}>
            <Form.Group className="mb-3">
              <Form.Label className="subaccount-modal__label">
                <IconMapPin size={16} />
                Segmento de Atuação *
              </Form.Label>
              <Controller
                name="business_segment"
                control={control}
                rules={{ required: "Segmento é obrigatório" }}
                render={({ field }) => (
                  <Form.Select
                    {...field}
                    className={`subaccount-modal__input ${
                      formErrors.business_segment ? "is-invalid" : ""
                    }`}
                  >
                    <option value="">Selecione o segmento</option>
                    {businessSegments.map((segment) => (
                      <option key={segment.value} value={segment.value}>
                        {segment.label}
                      </option>
                    ))}
                  </Form.Select>
                )}
              />
              {formErrors.business_segment && (
                <div className="invalid-feedback">
                  {formErrors.business_segment.message}
                </div>
              )}
            </Form.Group>
          </Col>

          <Col xs={12} md={6}>
            <Form.Group className="mb-3">
              <Form.Label className="subaccount-modal__label">
                <IconPhone size={16} />
                Telefone
              </Form.Label>
              <Controller
                name="phone"
                control={control}
                render={({ field }) => (
                  <NumberFormat
                    {...field}
                    format="(##) #####-####"
                    mask="_"
                    customInput={Form.Control}
                    placeholder="(11) 99999-9999"
                    className="subaccount-modal__input"
                  />
                )}
              />
            </Form.Group>
          </Col>

          <Col xs={12} md={6}>
            <Form.Group className="mb-3">
              <Form.Label className="subaccount-modal__label">
                <IconUsers size={16} />
                Máximo de Usuários
              </Form.Label>
              <Controller
                name="max_users_allowed"
                control={control}
                rules={{
                  min: {
                    value: 1,
                    message: "Mínimo de 1 usuário",
                  },
                  max: {
                    value: getUserLimitByPlan(),
                    message: `Máximo de ${getUserLimitByPlan()} usuários para seu plano`,
                  },
                }}
                render={({ field }) => (
                  <Form.Control
                    {...field}
                    type="number"
                    min="1"
                    max={getUserLimitByPlan()}
                    className={`subaccount-modal__input ${
                      formErrors.max_users_allowed ? "is-invalid" : ""
                    }`}
                  />
                )}
              />
              {formErrors.max_users_allowed && (
                <div className="invalid-feedback">
                  {formErrors.max_users_allowed.message}
                </div>
              )}
              <Form.Text className="text-muted">
                Máximo permitido para seu plano: {getUserLimitByPlan()} usuários
              </Form.Text>
            </Form.Group>
          </Col>

          <Col xs={12}>
            <Form.Group className="mb-3">
              <Form.Label className="subaccount-modal__label">
                Observações
              </Form.Label>
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <Form.Control
                    {...field}
                    as="textarea"
                    rows={3}
                    placeholder="Observações sobre o cliente, suas atividades e objetivos..."
                    className="subaccount-modal__input"
                    maxLength={500}
                  />
                )}
              />
              <Form.Text className="text-muted">
                {watchedValues.description?.length || 0}/500 caracteres
              </Form.Text>
            </Form.Group>
          </Col>
        </Row>
    </div>
  );

  // Formulário exclusivo para Pessoa Jurídica
  const renderJuridicalPersonForm = () => (
    <div className="subaccount-modal__step-content">
      <div className="subaccount-modal__step-header">
        <IconBuilding size={32} className="subaccount-modal__step-icon" />
        <h3>Dados da Empresa</h3>
        <p>Informações básicas da nova subconta</p>
      </div>

      {renderClientTypeSelector()}

      {/* Campos exclusivos para Pessoa Jurídica */}
      <Row>
        <Col xs={12} md={6}>
          <Form.Group className="mb-3">
            <Form.Label className="subaccount-modal__label">
              <IconBuilding size={16} />
              Razão Social *
            </Form.Label>
            <Controller
              name="company_name"
              control={control}
              rules={{
                required: clientType === "juridical" ? "Razão social é obrigatória" : false,
                minLength: {
                  value: 3,
                  message: "Razão social deve ter pelo menos 3 caracteres",
                },
                maxLength: {
                  value: 128,
                  message: "Razão social deve ter no máximo 128 caracteres",
                },
              }}
              render={({ field }) => (
                <Form.Control
                  {...field}
                  type="text"
                  placeholder="Ex: Empresa Subconta LTDA"
                  className={`subaccount-modal__input ${
                    formErrors.company_name ? "is-invalid" : ""
                  }`}
                  maxLength={128}
                />
              )}
            />
            {formErrors.company_name && (
              <div className="invalid-feedback">
                {formErrors.company_name.message}
              </div>
            )}
          </Form.Group>
        </Col>

        <Col xs={12} md={6}>
          <Form.Group className="mb-3">
            <Form.Label className="subaccount-modal__label">
              <IconMail size={16} />
              Email Corporativo *
            </Form.Label>
            <Controller
              name="email"
              control={control}
              rules={{
                required: clientType === "juridical" ? "Email é obrigatório" : false,
                validate: (value) => {
                  // Só validar quando for pessoa jurídica
                  if (clientType === "juridical" && value) {
                    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                    if (!emailRegex.test(value)) {
                      return "Email corporativo inválido";
                    }
                  }
                  return true;
                },
              }}
              render={({ field }) => (
                <Form.Control
                  {...field}
                  type="email"
                  placeholder="contato@empresa.com"
                  className={`subaccount-modal__input ${
                    formErrors.email ? "is-invalid" : ""
                  }`}
                />
              )}
            />
            {formErrors.email && (
              <div className="invalid-feedback">
                {formErrors.email.message}
              </div>
            )}
          </Form.Group>
        </Col>

        <Col xs={12} md={6}>
          <Form.Group className="mb-3">
            <Form.Label className="subaccount-modal__label">
              <IconId size={16} />
              CNPJ *
            </Form.Label>
            <Controller
              name="cpf_cnpj"
              control={control}
              rules={{
                required: clientType === "juridical" ? "CNPJ é obrigatório" : false,
                validate: (value) => {
                  // Só validar CNPJ quando for pessoa jurídica
                  if (clientType === "juridical") {
                    const clean = value?.replace(/\D/g, "") || "";
                    if (clean.length !== 14) {
                      return "CNPJ deve ter 14 dígitos";
                    }
                  }
                  return true;
                },
              }}
              render={({ field }) => (
                <NumberFormat
                  {...field}
                  format="##.###.###/####-##"
                  mask="_"
                  customInput={Form.Control}
                  placeholder="00.000.000/0000-00"
                  className={`subaccount-modal__input ${
                    formErrors.cpf_cnpj ? "is-invalid" : ""
                  }`}
                />
              )}
            />
            {formErrors.cpf_cnpj && (
              <div className="invalid-feedback">
                {formErrors.cpf_cnpj.message}
              </div>
            )}
          </Form.Group>
        </Col>

        <Col xs={12} md={6}>
          <Form.Group className="mb-3">
            <Form.Label className="subaccount-modal__label">
              <IconMapPin size={16} />
              Segmento de Negócio *
            </Form.Label>
            <Controller
              name="business_segment"
              control={control}
              rules={{ required: "Segmento é obrigatório" }}
              render={({ field }) => (
                <Form.Select
                  {...field}
                  className={`subaccount-modal__input ${
                    formErrors.business_segment ? "is-invalid" : ""
                  }`}
                >
                  <option value="">Selecione o segmento</option>
                  {businessSegments.map((segment) => (
                    <option key={segment.value} value={segment.value}>
                      {segment.label}
                    </option>
                  ))}
                </Form.Select>
              )}
            />
            {formErrors.business_segment && (
              <div className="invalid-feedback">
                {formErrors.business_segment.message}
              </div>
            )}
          </Form.Group>
        </Col>

        <Col xs={12} md={6}>
          <Form.Group className="mb-3">
            <Form.Label className="subaccount-modal__label">
              <IconPhone size={16} />
              Telefone Comercial
            </Form.Label>
            <Controller
              name="phone"
              control={control}
              render={({ field }) => (
                <NumberFormat
                  {...field}
                  format="(##) ####-####"
                  mask="_"
                  customInput={Form.Control}
                  placeholder="(11) 3333-4444"
                  className="subaccount-modal__input"
                />
              )}
            />
          </Form.Group>
        </Col>

        <Col xs={12} md={6}>
          <Form.Group className="mb-3">
            <Form.Label className="subaccount-modal__label">
              Tipo Jurídico
            </Form.Label>
            <Controller
              name="company_type"
              control={control}
              render={({ field }) => (
                <Form.Select {...field} className="subaccount-modal__input">
                  {companyTypes.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </Form.Select>
              )}
            />
            <Form.Text className="text-muted">
              Tipo jurídico da empresa (opcional)
            </Form.Text>
          </Form.Group>
        </Col>

        <Col xs={12} md={6}>
          <Form.Group className="mb-3">
            <Form.Label className="subaccount-modal__label">
              <IconUsers size={16} />
              Máximo de Usuários
            </Form.Label>
            <Controller
              name="max_users_allowed"
              control={control}
              rules={{
                min: {
                  value: 1,
                  message: "Mínimo de 1 usuário",
                },
                max: {
                  value: getUserLimitByPlan(),
                  message: `Máximo de ${getUserLimitByPlan()} usuários para seu plano`,
                },
              }}
              render={({ field }) => (
                <Form.Control
                  {...field}
                  type="number"
                  min="1"
                  max={getUserLimitByPlan()}
                  className={`subaccount-modal__input ${
                    formErrors.max_users_allowed ? "is-invalid" : ""
                  }`}
                />
              )}
            />
            {formErrors.max_users_allowed && (
              <div className="invalid-feedback">
                {formErrors.max_users_allowed.message}
              </div>
            )}
            <Form.Text className="text-muted">
              Máximo permitido para seu plano: {getUserLimitByPlan()} usuários
            </Form.Text>
          </Form.Group>
        </Col>

        <Col xs={12} md={6}>
          <Form.Group className="mb-3">
            <Form.Label className="subaccount-modal__label">
              Nome Fantasia
            </Form.Label>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <Form.Control
                  {...field}
                  type="text"
                  placeholder="Nome fantasia da empresa"
                  className="subaccount-modal__input"
                  maxLength={70}
                />
              )}
            />
            <Form.Text className="text-muted">
              Nome comercial da empresa (opcional)
            </Form.Text>
          </Form.Group>
        </Col>
      </Row>

      {/* Resumo das informações preenchidas */}
      {(watchedValues.company_name || watchedValues.email) && (
        <Alert variant="info" className="mt-4">
          <div className="d-flex align-items-center mb-2">
            <IconAlertCircle size={16} className="me-2" />
            <strong>Resumo dos dados preenchidos:</strong>
          </div>
          <Row>
            {watchedValues.company_name && (
              <Col md={6}>
                <small>
                  <strong>Empresa:</strong> {watchedValues.company_name}
                </small>
              </Col>
            )}
            {watchedValues.email && (
              <Col md={6}>
                <small>
                  <strong>Email:</strong> {watchedValues.email}
                </small>
              </Col>
            )}
            {watchedValues.cpf_cnpj && (
              <Col md={6}>
                <small>
                  <strong>CNPJ:</strong> {watchedValues.cpf_cnpj}
                </small>
              </Col>
            )}
            {watchedValues.business_segment && (
              <Col md={6}>
                <small>
                  <strong>Segmento:</strong>{" "}
                  {
                    businessSegments.find(
                      (s) => s.value === watchedValues.business_segment
                    )?.label
                  }
                </small>
              </Col>
            )}
          </Row>
        </Alert>
      )}
    </div>
  );

  // Função principal que alterna entre os formulários
  const renderCompanyStep = () => {
    if (clientType === "physical") {
      return renderPhysicalPersonForm();
    } else {
      return renderJuridicalPersonForm();
    }
  };

  const renderFeaturesStep = () => (
    <div className="subaccount-modal__step-content">
      <div className="subaccount-modal__step-header">
        <IconSettings size={32} className="subaccount-modal__step-icon" />
        <h3>Funcionalidades</h3>
        <p>Selecione as funcionalidades que a subconta poderá utilizar</p>
      </div>

      <div className="subaccount-modal__features-summary">
        <Alert variant="info">
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center">
              <IconAlertCircle size={16} className="me-2" />
              <strong>{selectedFeatures.size}</strong> funcionalidades
              selecionadas
            </div>
            <Button variant="outline-primary" size="sm" onClick={togglePreview}>
              <IconEye size={14} className="me-1" />
              {previewMode ? "Ocultar" : "Ver"} Preview
            </Button>
          </div>
        </Alert>
      </div>

      {previewMode && (
        <Alert variant="light" className="mb-4">
          <h6 className="mb-2">Preview das Funcionalidades Selecionadas:</h6>
          <div className="d-flex flex-wrap gap-2">
            {Array.from(selectedFeatures).map((featureId) => {
              const feature = availableFeatures.find((f) => f.id === featureId);
              const limit = featureLimits[featureId];
              return (
                <Badge key={featureId} bg="primary" className="p-2">
                  {feature?.name}
                  {limit && ` (${limit})`}
                </Badge>
              );
            })}
          </div>
        </Alert>
      )}

      {Object.keys(featuresGrouped).map((categoryName) => (
        <Card
          key={categoryName}
          className="subaccount-modal__feature-category mb-3"
        >
          <Card.Header className="subaccount-modal__category-header">
            <div className="d-flex justify-content-between align-items-center w-100">
              <h5 className="mb-0">{categoryName}</h5>
              <div className="d-flex align-items-center gap-2">
                <Badge variant="secondary">
                  {
                    featuresGrouped[categoryName].filter((f) =>
                      selectedFeatures.has(f.id)
                    ).length
                  }
                  /{featuresGrouped[categoryName].length}
                </Badge>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() =>
                    toggleAllFeaturesInCategory(featuresGrouped[categoryName])
                  }
                >
                  {featuresGrouped[categoryName].every((f) =>
                    selectedFeatures.has(f.id)
                  )
                    ? "Desmarcar Todos"
                    : "Marcar Todos"}
                </Button>
              </div>
            </div>
          </Card.Header>
          <Card.Body>
            <Row>
              {featuresGrouped[categoryName].map((feature) => (
                <Col xs={12} md={6} lg={4} key={feature.id} className="mb-3">
                  <div
                    className={`subaccount-modal__feature-item ${
                      selectedFeatures.has(feature.id) ? "selected" : ""
                    }`}
                    onClick={() => toggleFeature(feature.id)}
                  >
                    <div className="subaccount-modal__feature-header">
                      <div className="subaccount-modal__feature-toggle">
                        {selectedFeatures.has(feature.id) ? (
                          <IconToggleRight size={24} className="text-success" />
                        ) : (
                          <IconToggleLeft size={24} className="text-muted" />
                        )}
                      </div>
                      <div className="subaccount-modal__feature-info">
                        <strong>{feature.name}</strong>
                      </div>
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          </Card.Body>
        </Card>
      ))}

      {selectedFeatures.size === 0 && (
        <Alert variant="warning">
          <IconAlertCircle size={16} className="me-2" />
          Selecione pelo menos uma funcionalidade para continuar
        </Alert>
      )}
    </div>
  );

  const renderAdminStep = () => (
    <div className="subaccount-modal__step-content">
      <div className="subaccount-modal__step-header">
        <IconUserPlus size={32} className="subaccount-modal__step-icon" />
        <h3>Usuário Administrador</h3>
        <p>Criar o primeiro usuário admin da subconta</p>
      </div>

      <Row>
        <Col xs={12} md={6}>
          <Form.Group className="mb-3">
            <Form.Label className="subaccount-modal__label">
              <IconCrown size={16} />
              Nome *
            </Form.Label>
            <Controller
              name="admin_first_name"
              control={control}
              rules={{
                required: "Nome é obrigatório",
                minLength: {
                  value: 2,
                  message: "Nome deve ter pelo menos 2 caracteres",
                },
                pattern: {
                  value: /^[a-zA-ZÀ-ÿ\s]+$/,
                  message: "Nome deve conter apenas letras",
                },
              }}
              render={({ field }) => (
                <Form.Control
                  {...field}
                  type="text"
                  placeholder="Nome do administrador"
                  className={`subaccount-modal__input ${
                    formErrors.admin_first_name ? "is-invalid" : ""
                  }`}
                  maxLength={70}
                />
              )}
            />
            {formErrors.admin_first_name && (
              <div className="invalid-feedback">
                {formErrors.admin_first_name.message}
              </div>
            )}
          </Form.Group>
        </Col>

        <Col xs={12} md={6}>
          <Form.Group className="mb-3">
            <Form.Label className="subaccount-modal__label">
              Sobrenome
            </Form.Label>
            <Controller
              name="admin_last_name"
              control={control}
              rules={{
                pattern: {
                  value: /^[a-zA-ZÀ-ÿ\s]*$/,
                  message: "Sobrenome deve conter apenas letras",
                },
              }}
              render={({ field }) => (
                <Form.Control
                  {...field}
                  type="text"
                  placeholder="Sobrenome do administrador"
                  className={`subaccount-modal__input ${
                    formErrors.admin_last_name ? "is-invalid" : ""
                  }`}
                  maxLength={70}
                />
              )}
            />
            {formErrors.admin_last_name && (
              <div className="invalid-feedback">
                {formErrors.admin_last_name.message}
              </div>
            )}
          </Form.Group>
        </Col>

        <Col xs={12} md={6}>
          <Form.Group className="mb-3">
            <Form.Label className="subaccount-modal__label">
              <IconMail size={16} />
              Email *
            </Form.Label>
            <InputGroup>
              <Controller
                name="admin_email"
                control={control}
                rules={{
                  required: "Email é obrigatório",
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: "Email inválido",
                  },
                }}
                render={({ field }) => (
                  <Form.Control
                    {...field}
                    type="email"
                    placeholder="admin@empresa.com"
                    className={`subaccount-modal__input ${
                      formErrors.admin_email ? "is-invalid" : ""
                    }`}
                  />
                )}
              />
              {validatingEmail && (
                <InputGroup.Text>
                  <Spinner size="sm" />
                </InputGroup.Text>
              )}
            </InputGroup>
            {formErrors.admin_email && (
              <div className="invalid-feedback">
                {formErrors.admin_email.message}
              </div>
            )}
          </Form.Group>
        </Col>

        <Col xs={12} md={6}>
          <Form.Group className="mb-3">
            <Form.Label className="subaccount-modal__label">
              <IconPhone size={16} />
              Telefone *
            </Form.Label>
            <Controller
              name="admin_phone"
              control={control}
              rules={{
                required: "Telefone é obrigatório",
              }}
              render={({ field }) => (
                <NumberFormat
                  {...field}
                  format="(##) #####-####"
                  mask="_"
                  customInput={Form.Control}
                  placeholder="(11) 99999-9999"
                  className={`subaccount-modal__input ${
                    formErrors.admin_phone ? "is-invalid" : ""
                  }`}
                />
              )}
            />
            {formErrors.admin_phone && (
              <div className="invalid-feedback">
                {formErrors.admin_phone.message}
              </div>
            )}
          </Form.Group>
        </Col>

        <Col xs={12} md={6}>
          <Form.Group className="mb-3">
            <Form.Label className="subaccount-modal__label">
              <IconId size={16} />
              CPF *
            </Form.Label>
            <InputGroup>
              <Controller
                name="admin_cpf"
                control={control}
                rules={{
                  required: "CPF é obrigatório",
                  validate: (value) => {
                    if (value && value.length > 0) {
                      const clean = value.replace(/\D/g, "");
                      if (clean.length !== 11) {
                        return "CPF deve ter 11 dígitos";
                      }
                    }
                    return true;
                  },
                }}
                render={({ field }) => (
                  <NumberFormat
                    {...field}
                    format="###.###.###-##"
                    mask="_"
                    customInput={Form.Control}
                    placeholder="000.000.000-00"
                    className={`subaccount-modal__input ${
                      formErrors.admin_cpf ? "is-invalid" : ""
                    }`}
                  />
                )}
              />
              {validatingCPF && (
                <InputGroup.Text>
                  <Spinner size="sm" />
                </InputGroup.Text>
              )}
            </InputGroup>
            {formErrors.admin_cpf && (
              <div className="invalid-feedback">
                {formErrors.admin_cpf.message}
              </div>
            )}
           
          </Form.Group>
        </Col>

        <Col xs={12} md={6}>
          <Form.Group className="mb-3">
            <Form.Label className="subaccount-modal__label">
              Data de Nascimento
            </Form.Label>
            <Controller
              name="admin_birthday"
              control={control}
              rules={{
                validate: (value) => {
                  if (value) {
                    const birthDate = new Date(value);
                    const today = new Date();
                    const age = today.getFullYear() - birthDate.getFullYear();
                    if (age < 16) {
                      return "Administrador deve ter pelo menos 16 anos";
                    }
                    if (age > 120) {
                      return "Data de nascimento inválida";
                    }
                  }
                  return true;
                },
              }}
              render={({ field }) => (
                <Form.Control
                  {...field}
                  type="date"
                  className={`subaccount-modal__input ${
                    formErrors.admin_birthday ? "is-invalid" : ""
                  }`}
                  max={new Date().toISOString().split("T")[0]}
                />
              )}
            />
            {formErrors.admin_birthday && (
              <div className="invalid-feedback">
                {formErrors.admin_birthday.message}
              </div>
            )}
          </Form.Group>
        </Col>

        <Col xs={12}>
          <Card className="subaccount-modal__invite-card">
            <Card.Body>
              <div className="d-flex align-items-center">
                <IconMail size={24} className="text-primary me-3" />
                <div className="flex-grow-1">
                  <h6 className="mb-1">Enviar Convite por Email</h6>
                  <small className="text-muted">
                    O administrador receberá um email com instruções para
                    acessar a plataforma
                  </small>
                </div>
                <Controller
                  name="send_invitation"
                  control={control}
                  render={({ field }) => (
                    <Form.Check
                      type="switch"
                      checked={field.value}
                      onChange={field.onChange}
                      className="subaccount-modal__switch"
                      id="send_invitation_switch"
                    />
                  )}
                />
              </div>
              {watchedValues.send_invitation && (
                <Alert variant="info" className="mt-3 mb-0">
                  <small>
                    <IconAlertCircle size={14} className="me-1" />
                    Um email será enviado para{" "}
                    <strong>
                      {watchedValues.admin_email || "[email]"}
                    </strong>{" "}
                    com as credenciais de acesso.
                  </small>
                </Alert>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Preview do Admin */}
        {(watchedValues.admin_first_name || watchedValues.admin_email) && (
          <Col xs={12}>
            <Alert variant="light" className="mt-3">
              <h6 className="mb-2">Preview do Administrador:</h6>
              <Row>
                <Col md={6}>
                  <small>
                    <strong>Nome:</strong> {watchedValues.admin_first_name}{" "}
                    {watchedValues.admin_last_name || ""}
                  </small>
                </Col>
                <Col md={6}>
                  <small>
                    <strong>Email:</strong> {watchedValues.admin_email}
                  </small>
                </Col>
                {watchedValues.admin_phone && (
                  <Col md={6}>
                    <small>
                      <strong>Telefone:</strong> {watchedValues.admin_phone}
                    </small>
                  </Col>
                )}
                <Col md={6}>
                  <small>
                    <strong>Nível:</strong>{" "}
                    <Badge bg="warning">Administrador</Badge>
                  </small>
                </Col>
              </Row>
            </Alert>
          </Col>
        )}
      </Row>
    </div>
  );

  const renderReviewStep = () => {
    const displayName =
      clientType === "physical"
        ? watchedValues.physical_name
        : watchedValues.company_name;

    const displayEmail =
      clientType === "physical"
        ? watchedValues.physical_email
        : watchedValues.email;

    const displayDocument =
      clientType === "physical"
        ? watchedValues.physical_cpf
        : watchedValues.cpf_cnpj;

    return (
      <div className="subaccount-modal__step-content">
        <div className="subaccount-modal__step-header">
          <IconCheck size={32} className="subaccount-modal__step-icon" />
          <h3>Revisão Final</h3>
          <p>Confira todos os dados antes de criar a subconta</p>
        </div>

        <Row>
          <Col xs={12} md={6}>
            <Card className="subaccount-modal__review-card">
              <Card.Header>
                <h5>
                  {clientType === "physical" ? (
                    <>
                      <IconUser size={18} /> Dados Pessoais
                    </>
                  ) : (
                    <>
                      <IconBuilding size={18} /> Dados da Empresa
                    </>
                  )}
                </h5>
              </Card.Header>
              <Card.Body>
                <div className="subaccount-modal__review-item">
                  <strong>
                    {clientType === "physical" ? "Nome:" : "Razão Social:"}
                  </strong>
                  <span>{displayName}</span>
                </div>
                <div className="subaccount-modal__review-item">
                  <strong>Email:</strong>
                  <span>{displayEmail}</span>
                </div>
                <div className="subaccount-modal__review-item">
                  <strong>
                    {clientType === "physical" ? "CPF:" : "CNPJ:"}
                  </strong>
                  <span>{displayDocument}</span>
                </div>
                <div className="subaccount-modal__review-item">
                  <strong>Segmento:</strong>
                  <span>
                    {
                      businessSegments.find(
                        (s) => s.value === watchedValues.business_segment
                      )?.label
                    }
                  </span>
                </div>
                {clientType === "juridical" && (
                  <>
                    <div className="subaccount-modal__review-item">
                      <strong>Tipo:</strong>
                      <span>
                        {
                          companyTypes.find(
                            (t) => t.value === watchedValues.company_type
                          )?.label
                        }
                      </span>
                    </div>
                    {watchedValues.name && (
                      <div className="subaccount-modal__review-item">
                        <strong>Nome Fantasia:</strong>
                        <span>{watchedValues.name}</span>
                      </div>
                    )}
                  </>
                )}
                <div className="subaccount-modal__review-item">
                  <strong>Máx. Usuários:</strong>
                  <span>{watchedValues.max_users_allowed}</span>
                </div>
                {watchedValues.phone && (
                  <div className="subaccount-modal__review-item">
                    <strong>Telefone:</strong>
                    <span>{watchedValues.phone}</span>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>

          <Col xs={12} md={6}>
            <Card className="subaccount-modal__review-card">
              <Card.Header>
                <h5>
                  <IconUserPlus size={18} /> Administrador
                </h5>
              </Card.Header>
              <Card.Body>
                <div className="subaccount-modal__review-item">
                  <strong>Nome:</strong>
                  <span>
                    {watchedValues.admin_first_name}{" "}
                    {watchedValues.admin_last_name || ""}
                  </span>
                </div>
                <div className="subaccount-modal__review-item">
                  <strong>Email:</strong>
                  <span>{watchedValues.admin_email}</span>
                </div>
                <div className="subaccount-modal__review-item">
                  <strong>Telefone:</strong>
                  <span>{watchedValues.admin_phone}</span>
                </div>
                {watchedValues.admin_cpf && (
                  <div className="subaccount-modal__review-item">
                    <strong>CPF:</strong>
                    <span>{watchedValues.admin_cpf}</span>
                  </div>
                )}
                {watchedValues.admin_birthday && (
                  <div className="subaccount-modal__review-item">
                    <strong>Nascimento:</strong>
                    <span>
                      {new Date(
                        watchedValues.admin_birthday
                      ).toLocaleDateString("pt-BR")}
                    </span>
                  </div>
                )}
                <div className="subaccount-modal__review-item">
                  <strong>Nível:</strong>
                  <span>
                    <Badge bg="warning">Administrador</Badge>
                  </span>
                </div>
                <div className="subaccount-modal__review-item">
                  <strong>Convite por Email:</strong>
                  <span>
                    {watchedValues.send_invitation ? (
                      <Badge bg="success">Sim</Badge>
                    ) : (
                      <Badge bg="secondary">Não</Badge>
                    )}
                  </span>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col xs={12}>
            <Card className="subaccount-modal__review-card">
              <Card.Header>
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">
                    <IconSettings size={18} /> Funcionalidades (
                    {selectedFeatures.size})
                  </h5>
                  <Button
                    variant="outline-info"
                    size="sm"
                    onClick={() => setCurrentStep(2)}
                  >
                    <IconSettings size={14} className="me-1" />
                    Editar
                  </Button>
                </div>
              </Card.Header>
              <Card.Body>
                <div className="subaccount-modal__review-features">
                  {Object.keys(featuresGrouped).map((categoryName) => {
                    const categoryFeatures = featuresGrouped[
                      categoryName
                    ].filter((f) => selectedFeatures.has(f.id));
                    if (categoryFeatures.length === 0) return null;

                    return (
                      <div key={categoryName} className="mb-3">
                        <h6 className="text-muted mb-2">{categoryName}</h6>
                        <div className="d-flex flex-wrap gap-2">
                          {categoryFeatures.map((feature) => {
                            const limit = featureLimits[feature.id];
                            const note = featureNotes[feature.id];
                            return (
                              <div
                                key={feature.id}
                                className="subaccount-modal__feature-badge-container"
                              >
                                <Badge
                                  bg="primary"
                                  className="subaccount-modal__feature-badge"
                                  title={note || feature.description}
                                >
                                  {feature.name}
                                  {limit && ` (${limit})`}
                                </Badge>
                                {note && (
                                  <small
                                    className="text-muted d-block mt-1"
                                    style={{ fontSize: "0.7rem" }}
                                  >
                                    {note.substring(0, 50)}
                                    {note.length > 50 ? "..." : ""}
                                  </small>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Card.Body>
            </Card>
          </Col>

          {watchedValues.description && (
            <Col xs={12}>
              <Card className="subaccount-modal__review-card">
                <Card.Header>
                  <h5>
                    <IconAlertCircle size={18} />{" "}
                    {clientType === "physical"
                      ? "Observações"
                      : "Descrição da Empresa"}
                  </h5>
                </Card.Header>
                <Card.Body>
                  <p className="mb-0" style={{ lineHeight: 1.6 }}>
                    {watchedValues.description}
                  </p>
                </Card.Body>
              </Card>
            </Col>
          )}
        </Row>

        <Alert variant="success" className="mt-4">
          <div className="d-flex align-items-start">
            <IconSparkles size={20} className="me-2 mt-1" />
            <div>
              <strong>Tudo pronto!</strong> A subconta será criada com essas
              configurações.
              {watchedValues.send_invitation && (
                <div className="mt-1">
                  <small>
                    📧 Um email de convite será enviado para{" "}
                    <strong>{watchedValues.admin_email}</strong> com as
                    instruções de acesso.
                  </small>
                </div>
              )}
              <div className="mt-2">
                <small className="text-muted">
                  Após a criação, você poderá gerenciar esta subconta, modificar
                  funcionalidades e adicionar mais usuários.
                </small>
              </div>
            </div>
          </div>
        </Alert>
      </div>
    );
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="xl"
      centered
      className="subaccount-modal"
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header className="subaccount-modal__header">
        <div className="subaccount-modal__header-content">
          <div className="subaccount-modal__header-info">
            <Modal.Title className="subaccount-modal__title">
              <IconUserPlus size={24} />
              Criar Nova Subconta
            </Modal.Title>
          </div>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={handleClose}
            className="subaccount-modal__close-btn"
            disabled={loading}
          >
            <IconX size={16} />
          </Button>
        </div>
      </Modal.Header>

      <Modal.Body className="subaccount-modal__body">
        {loading && currentStep === 1 ? (
          <div className="subaccount-modal__loading">
            <Spinner animation="border" variant="primary" />
            <p>Carregando funcionalidades...</p>
          </div>
        ) : (
          <>
            <div className="mb-4">
              {/* Progress Steps */}
              <div className="subaccount-modal__steps">
                {Array.from({ length: totalSteps }, (_, i) => i + 1).map(
                  (step) => (
                    <div
                      key={step}
                      className={`subaccount-modal__step ${
                        step === currentStep
                          ? "subaccount-modal__step--active"
                          : ""
                      } ${
                        step < currentStep
                          ? "subaccount-modal__step--completed"
                          : ""
                      }`}
                      onClick={() => goToStep(step)}
                      style={{
                        cursor: step <= currentStep ? "pointer" : "default",
                      }}
                    >
                      <div className="subaccount-modal__step-number">
                        {step < currentStep ? <IconCheck size={14} /> : step}
                      </div>
                      <span className="subaccount-modal__step-label">
                        {stepTitles[step]}
                      </span>
                    </div>
                  )
                )}
              </div>

              {/* Progress Bar */}
              <ProgressBar
                now={(currentStep / totalSteps) * 100}
                className="subaccount-modal__progress"
              />
            </div>

            <Form onSubmit={handleSubmit(onSubmit)}>{renderStepContent()}</Form>
          </>
        )}
      </Modal.Body>

      <Modal.Footer className="subaccount-modal__footer">
        <div className="subaccount-modal__footer-actions">
          {currentStep > 1 && (
            <Button
              variant="outline-secondary"
              onClick={prevStep}
              disabled={loading || validatingSubaccountData}
              className="subaccount-modal__footer-btn"
              type="button"
            >
              <IconArrowLeft size={16} />
              Anterior
            </Button>
          )}

          <div className="subaccount-modal__footer-right">
            {currentStep < totalSteps ? (
              <Button
                variant="primary"
                onClick={nextStep}
                disabled={loading || validatingSubaccountData}
                className="subaccount-modal__footer-btn"
                type="button"
              >
                {validatingSubaccountData && currentStep === 1 ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Validando dados...
                  </>
                ) : (
                  <>
                    Próximo
                    <IconArrowRight size={16} />
                  </>
                )}
              </Button>
            ) : (
              <Button
                variant="success"
                onClick={handleSubmit(onSubmit)}
                disabled={loading || !isValid || selectedFeatures.size === 0}
                className="subaccount-modal__footer-btn"
                type="button"
              >
                {loading ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Criando...
                  </>
                ) : (
                  <>
                    <IconCheck size={16} />
                    Criar Subconta
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default CreateSubaccountModal;
